import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api.service';
import { getStoredCashSessionId, useCashSession } from '../components/CashSessionBar';
import { usePermissions } from '../context/PermissionsContext';

type Filters = {
  branchId: string;
  fromDate: string;
  toDate: string;
  status: string;
  voucherNumber: string;
  loanNumber: string;
  documentNumber: string;
};

const emptyFilters = (): Filters => ({
  branchId: '',
  fromDate: '',
  toDate: '',
  status: '',
  voucherNumber: '',
  loanNumber: '',
  documentNumber: '',
});

function hasActiveFilters(f: Filters) {
  return Boolean(
    f.branchId ||
      f.fromDate ||
      f.toDate ||
      f.status ||
      f.voucherNumber.trim() ||
      f.loanNumber.trim() ||
      f.documentNumber.trim(),
  );
}

export function Payments() {
  const { sessionId: cashSessionId } = useCashSession();
  const { canCritical } = usePermissions();
  const canVoid = canCritical('reverse_payment');

  const [payments, setPayments] = useState<any[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 25, pageCount: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchMode, setSearchMode] = useState(false);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [branches, setBranches] = useState<any[]>([]);
  const [loans, setLoans] = useState<any[]>([]);
  const [loanQuery, setLoanQuery] = useState('');
  const [loanSuggestions, setLoanSuggestions] = useState<any[]>([]);
  const [loanSearchBusy, setLoanSearchBusy] = useState(false);
  const [loanPickerOpen, setLoanPickerOpen] = useState(false);
  const loanSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<any | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  const [voidTarget, setVoidTarget] = useState<any | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidConfirmed, setVoidConfirmed] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [form, setForm] = useState({
    loanId: '',
    amount: '',
    paymentMethod: 'CASH',
    notes: '',
    splitPayment: false,
    method2: 'TRANSFER',
    amount2: '',
    transactionNumber: '',
    bankName: '',
  });

  const searchLoansForPayment = (q: string) => {
    if (loanSearchTimer.current) clearTimeout(loanSearchTimer.current);
    setLoanQuery(q);
    if (!q.trim()) {
      setLoanSuggestions([]);
      setLoanPickerOpen(false);
      return;
    }
    loanSearchTimer.current = setTimeout(async () => {
      setLoanSearchBusy(true);
      try {
        const { data } = await apiService.listLoans({
          q: q.trim(),
          page: 1,
          pageSize: 20,
          excludePending: true,
        });
        const active = (data ?? []).filter(
          (l: any) => l.status === 'APPROVED' || l.status === 'ACTIVE',
        );
        setLoanSuggestions(active);
        setLoanPickerOpen(true);
      } catch {
        setLoanSuggestions([]);
      } finally {
        setLoanSearchBusy(false);
      }
    }, 280);
  };

  const selectLoanForPayment = async (loan: any) => {
    setLoanPickerOpen(false);
    setLoanQuery(
      `${loan.customer?.firstName ?? ''} ${loan.customer?.lastName ?? ''} · DNI ${loan.customer?.documentNumber ?? '—'} · ${loan.loanNumber ?? loan.id.slice(0, 8)}`.trim(),
    );
    setForm((f) => ({ ...f, loanId: loan.id }));
    setLoans((prev) => {
      if (prev.some((l) => l.id === loan.id)) return prev;
      return [...prev, loan];
    });
    try {
      const full = await apiService.getLoan(loan.id);
      setLoans((prev) => prev.map((l) => (l.id === loan.id ? full : l)));
    } catch {
      /* keep list item */
    }
  };

  useEffect(() => {
    apiService.getBranches(true).then(setBranches).catch(() => {});
  }, []);

  const loadList = async (opts?: { page?: number; pageSize?: number }) => {
    const nextPage = opts?.page ?? page;
    const nextSize = opts?.pageSize ?? pageSize;
    setLoading(true);
    setError(null);
    try {
      const ps = await apiService.listPayments({ page: nextPage, pageSize: nextSize });
      setPayments(ps.data);
      setMeta(ps.meta);
      setPage(ps.meta.page);
      setPageSize(ps.meta.pageSize);
      setSearchMode(false);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  const runSearch = async (nextFilters: Filters = filters) => {
    if (!hasActiveFilters(nextFilters)) {
      await loadList({ page: 1, pageSize });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.searchPayments({
        branchId: nextFilters.branchId || undefined,
        fromDate: nextFilters.fromDate || undefined,
        toDate: nextFilters.toDate || undefined,
        status: nextFilters.status || undefined,
        voucherNumber: nextFilters.voucherNumber.trim() || undefined,
        loanNumber: nextFilters.loanNumber.trim() || undefined,
        documentNumber: nextFilters.documentNumber.trim() || undefined,
      });
      setPayments(data);
      setSearchMode(true);
      setMeta({
        total: data.length,
        page: 1,
        pageSize: data.length || 25,
        pageCount: 1,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchMode) return;
    loadList({ page, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const selectedLoan = useMemo(() => loans.find((l) => l.id === form.loanId), [loans, form.loanId]);
  const nextPending = useMemo(
    () => selectedLoan?.installments?.find((i: any) => i.status === 'PENDING' || i.status === 'OVERDUE'),
    [selectedLoan],
  );

  const runPreview = async () => {
    if (!form.loanId || !form.amount) return;
    setError(null);
    try {
      const res = await apiService.previewPayment({
        loanId: form.loanId,
        amount: parseFloat(form.amount),
      });
      setPreview(res);
    } catch (e: any) {
      setPreview(null);
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const usesCash =
    form.paymentMethod === 'CASH' || (form.splitPayment && form.method2 === 'CASH');
  const cashBlocked = usesCash && !cashSessionId;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setLastResult(null);
    try {
      if (cashBlocked) {
        throw new Error(
          'La caja está cerrada. Abrí la caja desde la barra superior e indicá el monto al abrir para cobrar en efectivo.',
        );
      }
      const amount = parseFloat(form.amount);
      const paymentMethods = form.splitPayment && form.amount2
        ? [
            { method: form.paymentMethod === 'BANK_TRANSFER' ? 'TRANSFER' : form.paymentMethod, amount: amount - parseFloat(form.amount2) },
            {
              method: form.method2,
              amount: parseFloat(form.amount2),
              transactionNumber: form.transactionNumber || undefined,
              bankName: form.bankName || undefined,
            },
          ]
        : undefined;

      const res = await apiService.createPayment({
        loanId: form.loanId,
        amount,
        paymentMethod: form.paymentMethod || undefined,
        notes: form.notes || undefined,
        cashSessionId: cashSessionId || undefined,
        paymentMethods,
      });
      setLastResult(res);
      setPreview(null);
      setForm({ loanId: '', amount: '', paymentMethod: 'CASH', notes: '', splitPayment: false, method2: 'TRANSFER', amount2: '', transactionNumber: '', bankName: '' });
      setShowForm(false);
      if (searchMode) {
        await runSearch();
      } else if (page === 1) {
        await loadList({ page: 1, pageSize });
      } else {
        setPage(1);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(' ') : (msg ?? e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmVoid = async () => {
    if (!voidTarget || voidReason.trim().length < 5 || !voidConfirmed) return;
    setVoiding(true);
    setError(null);
    try {
      const sid = cashSessionId || getStoredCashSessionId();
      await apiService.voidPayment(voidTarget.id, {
        reason: voidReason.trim(),
        cashSessionId: sid || undefined,
      });
      setVoidTarget(null);
      setVoidReason('');
      setVoidConfirmed(false);
      if (searchMode) await runSearch();
      else await loadList({ page, pageSize });
    } catch (err: any) {
      const msg = err?.friendlyMessage ?? err?.response?.data?.message ?? err?.message;
      setError(Array.isArray(msg) ? msg.join(' ') : String(msg ?? 'No se pudo anular el pago'));
    } finally {
      setVoiding(false);
    }
  };

  const clearFilters = async () => {
    setFilters(emptyFilters());
    setSearchMode(false);
    setPage(1);
    await loadList({ page: 1, pageSize });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pagos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cobrá en mostrador, buscá por DNI/comprobante y anulá cobros si hace falta.
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm((v) => {
              if (v) {
                setLoanQuery('');
                setLoanSuggestions([]);
                setLoanPickerOpen(false);
                setForm({
                  loanId: '',
                  amount: '',
                  paymentMethod: 'CASH',
                  notes: '',
                  splitPayment: false,
                  method2: 'TRANSFER',
                  amount2: '',
                  transactionNumber: '',
                  bankName: '',
                });
                setPreview(null);
              }
              return !v;
            });
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancelar cobro' : '+ Registrar pago'}
        </button>
      </div>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {lastResult && (
        <div className="mb-6 p-4 rounded-md bg-green-50 border border-green-200 text-green-900 text-sm">
          <p className="font-semibold">Pago registrado ✓</p>
          <p>Aplicado a {lastResult.applied?.length ?? 0} cuota(s).
            {' '}Capital: <code>${lastResult.principalAmount}</code>
            {' '}· Interés: <code>${lastResult.interestAmount}</code>
            {lastResult.penaltyAmount > 0 && <> · Punitorio: <code>${lastResult.penaltyAmount}</code></>}
          </p>
          {lastResult.remainingUnapplied > 0 && (
            <p>Excedente sin aplicar: <code>${lastResult.remainingUnapplied}</code></p>
          )}
          {lastResult.voucher?.voucherNumber && (
            <p>
              Comprobante N° <strong>{lastResult.voucher.voucherNumber}</strong>
              {' '}
              <button
                type="button"
                onClick={() => apiService.openVoucherPrint(lastResult.voucher.id)}
                className="text-blue-700 underline"
              >
                Imprimir
              </button>
            </p>
          )}
          <button onClick={() => setLastResult(null)} className="mt-2 text-xs underline">Cerrar</button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {cashBlocked && (
            <div className="md:col-span-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm">
              La caja está cerrada. Abrí una caja operativa desde la barra superior e indicá el monto al abrir para poder cobrar en efectivo.
            </div>
          )}
          {!cashBlocked && !cashSessionId && (
            <div className="md:col-span-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-sm">
              Sin caja abierta: los cobros que no sean en efectivo no requieren sesión de caja.
            </div>
          )}
          <div className="md:col-span-2 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Persona / préstamo</label>
            <input
              type="search"
              autoComplete="off"
              required={!form.loanId}
              value={loanQuery}
              onChange={(e) => {
                setForm((f) => ({ ...f, loanId: '' }));
                setPreview(null);
                searchLoansForPayment(e.target.value);
              }}
              onFocus={() => {
                if (loanSuggestions.length) setLoanPickerOpen(true);
              }}
              placeholder="Buscar por DNI, nombre o Nº de préstamo…"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {loanSearchBusy && (
              <p className="text-xs text-gray-500 mt-1">Buscando…</p>
            )}
            {loanPickerOpen && loanSuggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg text-sm">
                {loanSuggestions.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-0"
                      onClick={() => selectLoanForPayment(l)}
                    >
                      <span className="font-medium text-gray-900">
                        {l.customer?.firstName} {l.customer?.lastName}
                      </span>
                      <span className="text-gray-500">
                        {' '}
                        · DNI {l.customer?.documentNumber ?? '—'} · {l.loanNumber ?? l.id.slice(0, 8)} · $
                        {Number(l.amount).toLocaleString()} ({l.status})
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {loanPickerOpen && !loanSearchBusy && loanQuery.trim() && loanSuggestions.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">Sin préstamos activos para esa búsqueda.</p>
            )}
            {form.loanId && selectedLoan && (
              <p className="text-xs text-green-700 mt-1">
                Seleccionado: {selectedLoan.customer?.firstName} {selectedLoan.customer?.lastName}
                {selectedLoan.loanNumber ? ` · ${selectedLoan.loanNumber}` : ''}
              </p>
            )}
            {nextPending && (
              <p className="text-xs text-gray-600 mt-1">
                Próxima cuota a pagar: #{nextPending.installmentNumber} · total ${nextPending.totalAmount.toLocaleString()} · vence {nextPending.dueDate?.slice(0, 10)} ({nextPending.status})
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
            <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            {nextPending && (
              <button
                type="button"
                onClick={() => setForm({ ...form, amount: String(nextPending.totalAmount) })}
                className="mt-1 text-xs text-blue-600 hover:underline">
                Usar monto exacto (${nextPending.totalAmount.toLocaleString()})
              </button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Método</label>
            <select value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="CASH">Efectivo</option>
              <option value="BANK_TRANSFER">Transferencia</option>
              <option value="DEBIT_CARD">Débito</option>
              <option value="CREDIT_CARD">Crédito</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.splitPayment} onChange={(e) => setForm({ ...form, splitPayment: e.target.checked })} />
              Dividir en dos formas de pago
            </label>
          </div>
          {form.splitPayment && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Segunda forma</label>
                <select value={form.method2} onChange={(e) => setForm({ ...form, method2: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                  <option value="TRANSFER">Transferencia</option>
                  <option value="CASH">Efectivo</option>
                  <option value="CHECK">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Importe 2ª forma</label>
                <input type="number" step="0.01" value={form.amount2} onChange={(e) => setForm({ ...form, amount2: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nro transacción</label>
                <input value={form.transactionNumber} onChange={(e) => setForm({ ...form, transactionNumber: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                <input value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
              </div>
            </>
          )}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          {preview && (
            <div className="md:col-span-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-800">
              <p className="font-semibold mb-2">Preview de imputación</p>
              <ul className="space-y-1">
                {(preview.installments ?? []).map((s: any) => (
                  <li key={s.installmentId}>
                    Cuota #{s.installmentNumber}: punitorio ${s.penalty} · interés ${s.interest} · capital ${s.principal}
                    {' '}→ {s.status}
                  </li>
                ))}
              </ul>
              <p className="mt-2">
                Aplicado: <strong>${preview.appliedAmount}</strong>
                {preview.unappliedAmount > 0 ? (
                  <span>
                    {' '}
                    · Saldo a favor: <strong>${preview.unappliedAmount}</strong>
                  </span>
                ) : null}
              </p>
            </div>
          )}
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancelar</button>
            <button type="button" onClick={runPreview} className="px-4 py-2 border border-blue-600 text-blue-700 rounded-md hover:bg-blue-50">
              Preview imputación
            </button>
            <button
              type="submit"
              disabled={submitting || cashBlocked}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Registrando…' : 'Confirmar pago'}
            </button>
          </div>
        </form>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          void runSearch();
        }}
        className="mb-4 bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3"
      >
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">DNI cliente</label>
          <input
            value={filters.documentNumber}
            onChange={(e) => setFilters({ ...filters, documentNumber: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="12345678"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">N° comprobante</label>
          <input
            value={filters.voucherNumber}
            onChange={(e) => setFilters({ ...filters, voucherNumber: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">N° préstamo</label>
          <input
            value={filters.loanNumber}
            onChange={(e) => setFilters({ ...filters, loanNumber: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sucursal</label>
          <select
            value={filters.branchId}
            onChange={(e) => setFilters({ ...filters, branchId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Todas</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
          <input type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
          <input type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
            <option value="">Todos</option>
            <option value="COMPLETED">Completado</option>
            <option value="VOIDED">Anulado</option>
            <option value="PENDING">Pendiente</option>
          </select>
        </div>
        <div className="flex items-end gap-2">
          <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Buscando…' : 'Buscar'}
          </button>
          <button
            type="button"
            onClick={() => void clearFilters()}
            className="px-3 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50"
          >
            Limpiar
          </button>
        </div>
      </form>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando…</div>
        ) : payments.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {searchMode ? 'Sin resultados para esos filtros.' : 'No hay pagos registrados.'}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Préstamo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comp.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((p) => (
                <tr key={p.id} className={`hover:bg-gray-50 ${p.status === 'VOIDED' ? 'bg-red-50/40' : ''}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{p.paymentDate?.slice?.(0, 10) ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {p.customer ? (
                      <>
                        {p.customer.firstName} {p.customer.lastName}
                        {p.customer.documentNumber && (
                          <span className="block text-xs text-gray-500">DNI {p.customer.documentNumber}</span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                    {p.loan?.loanNumber ?? p.loanId?.slice?.(0, 8) ?? '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                    ${Number(p.amount).toLocaleString('es-AR')}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      p.status === 'VOIDED' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {p.status === 'VOIDED' ? 'Anulado' : (p.status ?? 'COMPLETED')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{p.paymentMethod ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-700">
                    {p.voucher?.voucherNumber ?? '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right space-x-3">
                    {p.voucher?.id && (
                      <button
                        type="button"
                        onClick={() => apiService.openVoucherPrint(p.voucher.id)}
                        className="text-blue-700 hover:underline"
                      >
                        Imprimir
                      </button>
                    )}
                    {canVoid && p.status === 'COMPLETED' && (
                      <button
                        type="button"
                        onClick={() => {
                          setVoidTarget(p);
                          setVoidReason('');
                          setVoidConfirmed(false);
                          setError(null);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!searchMode && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50 text-sm">
            <div className="text-gray-600">
              Página {meta.page} de {meta.pageCount} · {meta.total.toLocaleString('es-AR')} pagos
            </div>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="px-2 py-1 border rounded-md"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 border rounded-md disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={page >= meta.pageCount || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded-md disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
        {searchMode && (
          <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-600">
            {meta.total.toLocaleString('es-AR')} resultado(s) (máx. 200). Ajustá filtros para acotar.
          </div>
        )}
      </div>

      {voidTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="void-payment-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 id="void-payment-title" className="text-lg font-semibold text-gray-900 mb-2">
              ¿Estás seguro de anular este pago?
            </h2>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 mb-4">
              <p className="font-medium">Esta acción no se puede deshacer fácilmente.</p>
              <p className="mt-1">
                Cliente:{' '}
                <strong>
                  {[voidTarget.customer?.firstName, voidTarget.customer?.lastName]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </strong>
                {voidTarget.customer?.documentNumber
                  ? ` · DNI ${voidTarget.customer.documentNumber}`
                  : ''}
              </p>
              <p>
                Préstamo <strong>{voidTarget.loan?.loanNumber ?? '—'}</strong>
                {' · '}
                Monto{' '}
                <strong>
                  $
                  {Number(voidTarget.amount ?? 0).toLocaleString('es-AR', {
                    minimumFractionDigits: 2,
                  })}
                </strong>
              </p>
              <p>
                Comprobante{' '}
                <strong>{voidTarget.voucher?.voucherNumber ?? voidTarget.id}</strong>
              </p>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de anulación (obligatorio)
            </label>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
              placeholder="Ej: cobro duplicado, error de imputación…"
            />
            <label className="flex items-start gap-2 text-sm text-gray-800 mb-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={voidConfirmed}
                onChange={(e) => setVoidConfirmed(e.target.checked)}
              />
              <span>
                Confirmo que quiero anular este pago. Se revertirán cuotas, asientos y puntos
                asociados.
              </span>
            </label>
            <p className="text-xs text-gray-500 mb-4">
              Si el cobro fue en caja cerrada, usá tu sesión abierta actual.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setVoidTarget(null);
                  setVoidReason('');
                  setVoidConfirmed(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700"
              >
                No, cancelar
              </button>
              <button
                type="button"
                disabled={voiding || voidReason.trim().length < 5 || !voidConfirmed}
                onClick={() => void confirmVoid()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {voiding ? 'Anulando…' : 'Sí, anular pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
